import { Head, router } from "@inertiajs/react";
import { Loader2, RefreshCw, Upload, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { OnboardingLayout } from "@/components/onboarding/onboarding-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  continueOnboardingBrandingPath,
  regenerateOnboardingBrandingPath,
  startOnboardingBrandingPath,
} from "@/routes";

type StepKey = "connect_domain" | "sign_up" | "branding" | "api_key";

type OnboardingStepStatus = "pending" | "completed" | "skipped";

interface OnboardingStepState {
  key: StepKey;
  status: OnboardingStepStatus;
  completedAt?: string;
}

interface DomainSummary {
  id: number;
  hostname: string;
  customLogoUrl?: string | null;
  bg_color_hex?: string | null;
  text_color_hex?: string | null;
}

interface LogoCandidate {
  url: string;
  source: string;
}

type BrandingExtractionStatus = "pending" | "processing" | "completed" | "failed";

interface BrandingExtraction {
  id: number;
  status: BrandingExtractionStatus;
  stage: string;
  logoCandidates: LogoCandidate[];
  suggestedBgColorHex?: string | null;
  suggestedTextColorHex?: string | null;
  draftBgColorHex?: string | null;
  draftTextColorHex?: string | null;
  draftLogoUrl?: string | null;
  previewImageUrl?: string | null;
  previewStatus?: string | null;
  errorMessage?: string | null;
}

interface BrandingStepProps {
  domain?: DomainSummary | null;
  onboarding: {
    currentStep: StepKey;
    steps: OnboardingStepState[];
  };
  extraction?: BrandingExtraction | null;
  startError?: string | null;
}

function normalizeHex(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return "";
  const normalized = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  return /^[0-9a-f]{6}$/i.test(normalized.slice(1)) ? normalized.toUpperCase() : normalized;
}

function stageLabel(stage?: string) {
  switch (stage) {
    case "pending":
      return "Starting…";
    case "scraping":
      return "Scanning your homepage…";
    case "logos":
      return "Finding logo options…";
    case "colors":
      return "Extracting brand colors…";
    case "preview":
    case "preview_rendering":
      return "Rendering a sample…";
    case "completed":
      return "Ready";
    case "failed":
      return "Failed";
    default:
      return "Working…";
  }
}

export default function OnboardingBrandingPage({ domain, onboarding, extraction }: BrandingStepProps) {
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);

  const [bgColorHex, setBgColorHex] = useState("");
  const [textColorHex, setTextColorHex] = useState("");
  const [selectedLogoUrl, setSelectedLogoUrl] = useState<string | null>(null);

  const [uploadedLogo, setUploadedLogo] = useState<File | null>(null);
  const [uploadedLogoName, setUploadedLogoName] = useState("");
  const [uploadedLogoPreview, setUploadedLogoPreview] = useState<string | null>(null);
  const [uploadedLogoPreviewed, setUploadedLogoPreviewed] = useState(true);

  const [hasTouchedColors, setHasTouchedColors] = useState(false);
  const [hasTouchedLogoSelection, setHasTouchedLogoSelection] = useState(false);

  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerateError, setRegenerateError] = useState<string | null>(null);

  const [isContinuing, setIsContinuing] = useState(false);

  const initializedFromExtraction = useRef(false);

  const rootHost = useMemo(() => {
    return domain?.hostname.replace(/^\*\./, "") ?? "";
  }, [domain]);

  const startExtraction = useCallback(() => {
    if (!domain) return;

    setIsStarting(true);
    setStartError(null);

    try {
      router.post(
        startOnboardingBrandingPath(),
        {
          domain_id: domain.id,
        },
        {
          preserveState: true,
          preserveUrl: true,
          onFinish: () => setIsStarting(false),
        },
      );
    } catch {
      setStartError("We couldn’t start branding extraction. Please try again.");
    }
  }, [domain]);

  useEffect(() => {
    if (!domain) return;
    startExtraction();
  }, []);

  const shouldPoll = useMemo(() => {
    if (!domain) return false;
    if (!extraction) return true;
    if (extraction.status === "processing") return true;
    if (!extraction.previewImageUrl) return true;
    if (extraction.previewStatus && extraction.previewStatus !== "completed") return true;
    return false;
  }, [domain, extraction]);

  useEffect(() => {
    if (!shouldPoll) return;

    const interval = window.setInterval(() => {
      router.reload({ only: ["onboarding", "extraction"] });
    }, 1500);

    return () => window.clearInterval(interval);
  }, [shouldPoll]);

  useEffect(() => {
    if (!extraction || initializedFromExtraction.current) return;

    const initialBg = extraction.draftBgColorHex || extraction.suggestedBgColorHex || domain?.bg_color_hex || "";
    const initialText =
      extraction.draftTextColorHex || extraction.suggestedTextColorHex || domain?.text_color_hex || "";
    const initialLogo = extraction.draftLogoUrl || extraction.logoCandidates?.[0]?.url || null;

    setBgColorHex(initialBg);
    setTextColorHex(initialText);
    setSelectedLogoUrl(initialLogo);

    initializedFromExtraction.current = true;
  }, [extraction, domain]);

  useEffect(() => {
    if (!extraction) return;

    if (!hasTouchedColors) {
      if (!bgColorHex && extraction.suggestedBgColorHex) {
        setBgColorHex(extraction.suggestedBgColorHex);
      }
      if (!textColorHex && extraction.suggestedTextColorHex) {
        setTextColorHex(extraction.suggestedTextColorHex);
      }
    }

    if (!hasTouchedLogoSelection && !uploadedLogo) {
      if (!selectedLogoUrl && extraction.draftLogoUrl) {
        setSelectedLogoUrl(extraction.draftLogoUrl);
      } else if (!selectedLogoUrl && extraction.logoCandidates?.[0]?.url) {
        setSelectedLogoUrl(extraction.logoCandidates[0].url);
      }
    }
  }, [bgColorHex, extraction, hasTouchedColors, hasTouchedLogoSelection, selectedLogoUrl, textColorHex, uploadedLogo]);

  const previewBg = useMemo(() => {
    const normalized = normalizeHex(bgColorHex);
    return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : "#0B1220";
  }, [bgColorHex]);

  const previewText = useMemo(() => {
    const normalized = normalizeHex(textColorHex);
    return /^#[0-9A-F]{6}$/.test(normalized) ? normalized : "#FFFFFF";
  }, [textColorHex]);

  const isDirtyForPreview = useMemo(() => {
    if (!extraction) return false;

    const currentBg = normalizeHex(bgColorHex);
    const currentText = normalizeHex(textColorHex);
    const draftBg = normalizeHex(extraction.draftBgColorHex || "");
    const draftText = normalizeHex(extraction.draftTextColorHex || "");

    const bgDirty = currentBg !== draftBg;
    const textDirty = currentText !== draftText;

    const logoDirty =
      uploadedLogo !== null ? !uploadedLogoPreviewed : (selectedLogoUrl || null) !== (extraction.draftLogoUrl || null);

    return bgDirty || textDirty || logoDirty;
  }, [bgColorHex, extraction, selectedLogoUrl, textColorHex, uploadedLogo, uploadedLogoPreviewed]);

  const handleLogoCandidateSelect = (url: string) => {
    setHasTouchedLogoSelection(true);
    setSelectedLogoUrl(url);
    setUploadedLogo(null);
    setUploadedLogoName("");
    setUploadedLogoPreview(null);
    setUploadedLogoPreviewed(true);
  };

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setHasTouchedLogoSelection(true);
    setUploadedLogo(file);
    setUploadedLogoName(file.name);
    setUploadedLogoPreviewed(false);

    const reader = new FileReader();
    reader.onloadend = () => {
      setUploadedLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogoSelection = () => {
    setUploadedLogo(null);
    setUploadedLogoName("");
    setUploadedLogoPreview(null);
    setUploadedLogoPreviewed(true);
  };

  const handleRegeneratePreview = useCallback(() => {
    if (!domain) return;
    if (!extraction) return;

    setIsRegenerating(true);
    setRegenerateError(null);

    router.post(
      regenerateOnboardingBrandingPath(),
      {
        domain_id: domain.id,
        bg_color_hex: normalizeHex(bgColorHex),
        text_color_hex: normalizeHex(textColorHex),
        selected_logo_url: uploadedLogo ? "" : selectedLogoUrl || "",
        custom_logo: uploadedLogo,
      },
      {
        preserveState: true,
        preserveUrl: true,
        onFinish: () => setIsRegenerating(false),
      },
    );
    if (uploadedLogo) setUploadedLogoPreviewed(true);
  }, [bgColorHex, domain, extraction, selectedLogoUrl, textColorHex, uploadedLogo]);

  const handleContinue = () => {
    if (!domain) return;

    setIsContinuing(true);

    router.post(
      continueOnboardingBrandingPath(),
      {
        domain_id: domain.id,
        bg_color_hex: normalizeHex(bgColorHex),
        text_color_hex: normalizeHex(textColorHex),
        selected_logo_url: uploadedLogo ? "" : selectedLogoUrl || "",
        custom_logo: uploadedLogo,
      },
      {
        preserveState: true,
        onFinish: () => setIsContinuing(false),
      },
    );
  };

  const extractionBadge = useMemo(() => {
    if (!extraction) return null;

    const label = stageLabel(extraction.stage);
    if (extraction.status === "failed") {
      return <Badge variant="destructive">{label}</Badge>;
    }

    if (extraction.status === "completed") {
      return <Badge variant="success">{label}</Badge>;
    }

    return <Badge variant="secondary">{label}</Badge>;
  }, [extraction]);

  return (
    <>
      <Head title="Choose Branding - Onboarding" />

      <OnboardingLayout
        title="Branding"
        description="We’ll suggest a logo and colors from your homepage so you can use these as defaults for your OG images. Previews will look consistent across pages."
        onboarding={onboarding}
        onSkipStep={() => router.post("/onboarding/steps/branding/skip")}
        onContinueStep={() => void handleContinue()}
      >
        <div className="space-y-6">
          {!domain ? (
            <Card>
              <CardHeader>
                <CardTitle>No domain connected</CardTitle>
                <CardDescription>Add a domain first so we can detect your branding.</CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <>
              <Card>
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <CardTitle>{domain.hostname}</CardTitle>
                    <CardDescription>
                      We’re extracting your logo and colors from <code className="font-mono">https://{rootHost}/</code>.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {extractionBadge}
                    {isStarting && <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />}
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {startError && (
                    <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border p-3 text-sm">
                      {startError}
                    </div>
                  )}
                  {extraction?.errorMessage && (
                    <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border p-3 text-sm">
                      {extraction.errorMessage}
                    </div>
                  )}
                  <div
                    className="rounded-lg border p-4 transition-colors"
                    style={{ backgroundColor: previewBg, color: previewText }}
                  >
                    <p className="text-sm font-semibold">Color preview</p>
                    <p className="text-sm opacity-90">This is how text will look on the chosen background.</p>
                  </div>
                </CardContent>
              </Card>

              <div className="grid gap-6 lg:grid-cols-2">
                <div className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Colors</CardTitle>
                      <CardDescription>These are applied domain-wide during crawling.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label htmlFor="domain-bg-color-hex">Background</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              aria-label="Background color picker"
                              type="color"
                              value={previewBg}
                              onChange={(e) => {
                                setHasTouchedColors(true);
                                setBgColorHex(e.target.value);
                              }}
                              className="h-9 w-12 cursor-pointer p-1"
                            />
                            <Input
                              id="domain-bg-color-hex"
                              value={bgColorHex}
                              placeholder="#0B1220"
                              onChange={(e) => {
                                setHasTouchedColors(true);
                                setBgColorHex(e.target.value);
                              }}
                              onBlur={() => setBgColorHex(normalizeHex(bgColorHex))}
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="domain-text-color-hex">Text</Label>
                          <div className="flex items-center gap-2">
                            <Input
                              aria-label="Text color picker"
                              type="color"
                              value={previewText}
                              onChange={(e) => {
                                setHasTouchedColors(true);
                                setTextColorHex(e.target.value);
                              }}
                              className="h-9 w-12 cursor-pointer p-1"
                            />
                            <Input
                              id="domain-text-color-hex"
                              value={textColorHex}
                              placeholder="#FFFFFF"
                              onChange={(e) => {
                                setHasTouchedColors(true);
                                setTextColorHex(e.target.value);
                              }}
                              onBlur={() => setTextColorHex(normalizeHex(textColorHex))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setHasTouchedColors(true);
                            setBgColorHex("");
                            setTextColorHex("");
                          }}
                          disabled={isContinuing}
                        >
                          Reset
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Logo</CardTitle>
                      <CardDescription>Pick one of the detected options or upload your own.</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {domain.customLogoUrl && !uploadedLogoPreview && (
                        <div className="space-y-2">
                          <p className="text-muted-foreground text-xs">Current domain logo</p>
                          <div className="relative aspect-square max-w-[160px] overflow-hidden rounded-lg border bg-transparent">
                            <img
                              src={domain.customLogoUrl}
                              alt="Current domain logo"
                              className="h-full w-full object-contain p-2"
                            />
                          </div>
                        </div>
                      )}

                      {extraction?.logoCandidates?.length ? (
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                          {extraction.logoCandidates.map((candidate) => {
                            const selected = selectedLogoUrl === candidate.url;
                            return (
                              <button
                                key={candidate.url}
                                type="button"
                                onClick={() => handleLogoCandidateSelect(candidate.url)}
                                className={cn(
                                  "hover:border-primary/60 rounded-lg border bg-white p-3 text-left transition-colors dark:bg-gray-900",
                                  selected ? "border-primary ring-primary/10 ring-2" : "border-border",
                                )}
                              >
                                <div className="flex items-center justify-center">
                                  <img src={candidate.url} alt="Logo option" className="h-12 w-12 object-contain" />
                                </div>
                                <p className="text-muted-foreground mt-2 truncate text-xs">
                                  {candidate.source.replace(/_/g, " ").trim()}
                                </p>
                              </button>
                            );
                          })}
                        </div>
                      ) : extraction?.status === "processing" ? (
                        <div className="text-muted-foreground flex items-center gap-2 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Finding logo options…
                        </div>
                      ) : null}

                      <div className="border-border hover:border-primary/50 group cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-colors">
                        <input
                          type="file"
                          onChange={handleLogoUpload}
                          accept="image/*"
                          className="hidden"
                          id="domain-branding-logo-upload"
                        />
                        <label htmlFor="domain-branding-logo-upload" className="block cursor-pointer">
                          <Upload className="text-muted-foreground group-hover:text-primary mx-auto mb-2 h-8 w-8 transition-colors" />
                          <p className="text-foreground text-sm font-medium">
                            {uploadedLogoName ? uploadedLogoName : "Click to upload"}
                          </p>
                          <p className="text-muted-foreground text-xs">PNG, JPG, SVG, WebP, AVIF up to 5MB</p>
                        </label>
                      </div>

                      {uploadedLogoName && (
                        <div className="space-y-2">
                          {uploadedLogoPreview && (
                            <div className="relative aspect-square max-w-[160px] overflow-hidden rounded-lg border bg-transparent">
                              <img
                                src={uploadedLogoPreview}
                                alt="Uploaded logo preview"
                                className="h-full w-full object-contain p-2"
                              />
                            </div>
                          )}
                          <div className="bg-secondary/50 flex items-center justify-between gap-2 rounded-lg p-3">
                            <span className="text-foreground truncate text-sm">{uploadedLogoName}</span>
                            <button
                              type="button"
                              onClick={handleRemoveLogoSelection}
                              className="hover:bg-secondary rounded p-1 transition-colors"
                            >
                              <X className="text-muted-foreground h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
                <Card className="flex flex-col">
                  <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <CardTitle>Sample preview</CardTitle>
                      <CardDescription>
                        This is a lightweight sample using your current selections. We&apos;ll regenerate when you
                        update your branding.
                      </CardDescription>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => void handleRegeneratePreview()}
                      disabled={!isDirtyForPreview || isRegenerating}
                    >
                      {isRegenerating ? (
                        <span className="flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Regenerating…
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <RefreshCw className="h-4 w-4" />
                          Regenerate preview
                        </span>
                      )}
                    </Button>
                  </CardHeader>
                  <CardContent className="flex flex-1 flex-col items-center justify-center space-y-4">
                    {regenerateError && (
                      <div className="border-destructive/40 bg-destructive/10 text-destructive w-full rounded-md border p-3 text-sm">
                        {regenerateError}
                      </div>
                    )}

                    <div className="bg-muted/30 aspect-[1200/630] w-full max-w-2xl overflow-hidden rounded-lg border">
                      {extraction?.previewImageUrl ? (
                        <img
                          src={extraction.previewImageUrl}
                          alt="Sample social image preview"
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <div className="text-muted-foreground flex h-full w-full items-center justify-center gap-2 text-sm">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {extraction ? stageLabel(extraction.stage) : "Loading…"}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </OnboardingLayout>
    </>
  );
}
